import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { pipelineService } from '../services/pipelineService';
import { runProgressReducer, initialRunState } from './runProgress';

const SSE_EVENTS = ['step_start', 'progress', 'step_end', 'done'];

// Ticks once a second while a run is live, so the elapsed clock moves without
// re-rendering anything else.
export function useElapsed(startedAt, active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active || !startedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active, startedAt]);
  return startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0;
}

// Owns one run's SSE subscription and folds its events into progress state.
//
// The portal keeps no replay buffer, so re-attaching to a run that is already in
// flight (after a reload) shows only the events that arrive from now on — the
// step list starts part-way through rather than replaying from the beginning.
export function useRunStream({ onFinish } = {}) {
  const [state, dispatch] = useReducer(runProgressReducer, initialRunState);
  const [reattached, setReattached] = useState(false);
  const esRef = useRef(null);
  const settledRef = useRef(false); // a terminal `done` already landed
  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;

  const close = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
  }, []);

  useEffect(() => close, [close]);

  const attach = useCallback(
    (runId) => {
      close();
      settledRef.current = false;
      const es = new EventSource(pipelineService.streamUrl(runId));
      esRef.current = es;
      SSE_EVENTS.forEach((type) =>
        es.addEventListener(type, (e) => {
          let payload = {};
          try {
            payload = JSON.parse(e.data);
          } catch (_) {
            /* a malformed frame must not kill the stream */
          }
          dispatch({ ...payload, type });
          if (type === 'done') {
            settledRef.current = true;
            close();
            if (finishRef.current) finishRef.current(payload.status || 'success');
          }
        })
      );
      // The browser also fires onerror when the server closes a finished stream.
      // Only a drop *before* the terminal `done` is a real failure.
      es.onerror = () => {
        if (settledRef.current) return;
        close();
        dispatch({ type: 'done', status: 'failed', error: 'Lost connection to the import.' });
      };
    },
    [close]
  );

  // Kick off a run, then follow it. `starter` resolves to the new run id.
  const startRun = useCallback(
    async (starter) => {
      setReattached(false);
      dispatch({ type: 'start', startedAt: Date.now() });
      const runId = await starter();
      attach(runId);
      return runId;
    },
    [attach]
  );

  // Follow a run that was already going when the page loaded.
  const followExisting = useCallback(
    (runId, startedAt) => {
      setReattached(true);
      dispatch({ type: 'start', startedAt: startedAt || Date.now() });
      attach(runId);
    },
    [attach]
  );

  return { state, startRun, followExisting, close, reattached };
}
